/* Saved shelf: browser-saved handles rendered as native product cards. */
(() => {
  const CONCURRENCY = 4;
  const TIMEOUT = 10000;
  const CARD = '[data-saved-card][data-saved-card-version="1"]';
  const UNSAFE = "script, iframe, object, embed, link, style, meta, base, form";
  const URL_ATTRIBUTES = ["href", "src", "action", "formaction", "xlink:href"];
  const normalize = (value) =>
    value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  const fill = (template, token, value) =>
    template.replace(`{{ ${token} }}`, () => String(value));
  const safeURL = (value) => {
    try {
      const url = new window.URL(value, window.location.href);
      return ["http:", "https:"].includes(url.protocol);
    } catch {
      return false;
    }
  };
  const isAbort = (signal, generation, owner) =>
    signal.aborted || generation !== owner.generation || !owner.isConnected;

  class StorefrontSavedPage extends window.HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.grid = this.querySelector("[data-saved-grid]");
      if (!this.grid) return;
      this.toolbar = this.querySelector("[data-saved-toolbar]");
      this.form = this.querySelector("[data-saved-form]");
      this.query = this.querySelector("[data-saved-query]");
      this.collection = this.querySelector("[data-saved-collection]");
      this.sort = this.querySelector("[data-saved-sort]");
      this.count = this.querySelector("[data-saved-results-count]");
      this.pick = this.querySelector("[data-saved-pick]");
      this.picked = this.querySelector("[data-saved-picked]");
      this.status = this.querySelector("[data-saved-status]");
      const root = window.Shopify?.routes?.root || "/";
      this.root = root.endsWith("/") ? root : `${root}/`;
      this.cache ||= new Map();
      this.items ||= new Map();
      this.queue = [];
      this.active = new Map();
      this.generation ??= 0;
      this.listeners = new window.AbortController();
      this.bind(this.listeners.signal);
      if (window.StorefrontSaved)
        this.sync(window.StorefrontSaved.getHandles());
      else this.renderState();
    }
    disconnectedCallback() {
      this.cancelAll();
      this.listeners?.abort();
      this.listeners = null;
    }
    bind(signal) {
      const options = { signal };
      window.document.addEventListener(
        "sf:saved-change",
        (event) =>
          this.sync(
            Array.isArray(event.detail?.handles)
              ? event.detail.handles
              : window.StorefrontSaved?.getHandles() || [],
          ),
        options,
      );
      // Theme Editor section reloads: re-render this shelf's state.
      window.document.addEventListener(
        "shopify:section:load",
        () => this.renderState(),
        options,
      );
      this.addEventListener("click", (event) => this.click(event), options);
      this.form?.addEventListener(
        "submit",
        (event) => event.preventDefault(),
        options,
      );
      this.form?.addEventListener(
        "reset",
        (event) => {
          event.preventDefault();
          this.resetFilters();
        },
        options,
      );
      this.query?.addEventListener("input", () => this.filter(), options);
      for (const select of [this.collection, this.sort])
        select?.addEventListener("change", () => this.filter(), options);
    }
    click(event) {
      const link = event.target.closest("a[data-saved-collection-link]");
      if (link) return this.chooseCollection(event, link);
      const retry = event.target.closest("[data-saved-retry]");
      if (retry) {
        event.preventDefault();
        const handle = retry.closest("[data-saved-error]")?.dataset.savedHandle;
        if (!handle || retry.getAttribute("aria-disabled") === "true") return;
        retry.setAttribute("aria-disabled", "true");
        this.enqueue(handle);
      } else if (event.target.closest("[data-saved-clear]")) {
        event.preventDefault();
        this.resetFilters();
        this.query?.focus();
      } else if (event.target.closest("[data-saved-pick]")) {
        event.preventDefault();
        this.pickOne();
      }
    }
    chooseCollection(event, link) {
      const value = link.dataset.savedCollectionLink;
      const known = [...(this.collection?.options || [])].some(
        (option) => option.value === value,
      );
      if (
        !known ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      event.preventDefault();
      this.collection.value = value;
      this.filter();
    }
    renderCollectionLinks() {
      const value = this.collection?.value || "all";
      this.querySelectorAll("a[data-saved-collection-link]").forEach((link) => {
        if (link.dataset.savedCollectionLink === value)
          link.setAttribute("aria-current", "true");
        else link.removeAttribute("aria-current");
      });
    }
    sync(handles) {
      if (!this.listeners) return;
      this.handles = handles.filter((handle) => typeof handle === "string");
      this.ready = true;
      const saved = new Set(this.handles);
      const removed = [...this.items.keys()].filter((key) => !saved.has(key));
      for (const handle of [...this.queue, ...this.active.keys()])
        if (!saved.has(handle)) this.cancel(handle);
      const focusTarget = this.focusAfterRemoval(removed);
      for (const handle of removed) {
        if (this.items.get(handle) === this.pickedItem) this.clearPick();
        this.items.get(handle).remove();
        this.items.delete(handle);
      }
      for (const handle of this.handles) {
        if (this.items.has(handle)) continue;
        if (this.cache.has(handle)) this.place(handle, this.cache.get(handle));
        else this.enqueue(handle);
      }
      this.pump();
      this.apply();
      focusTarget?.()?.focus({ preventScroll: true });
    }
    focusAfterRemoval(removed) {
      const active = window.document.activeElement;
      const removedNodes = removed.map((handle) => this.items.get(handle));
      const focused = removedNodes.find((node) => node.contains(active));
      if (!focused) return null;
      const remaining = [...this.grid.children].filter(
        (node) =>
          node.dataset.savedHandle &&
          !node.hidden &&
          !removedNodes.includes(node),
      );
      const after = remaining.find(
        (node) =>
          focused.compareDocumentPosition(node) &
          window.Node.DOCUMENT_POSITION_FOLLOWING,
      );
      const before = remaining
        .filter(
          (node) =>
            focused.compareDocumentPosition(node) &
            window.Node.DOCUMENT_POSITION_PRECEDING,
        )
        .pop();
      const item = after || before;
      const target =
        item?.querySelector("[data-saved-toggle]") ||
        item?.querySelector("[data-saved-retry]") ||
        item?.querySelector("a[href]");
      // Resolved after the state renders so the empty state can take focus.
      return () => target || this.fallbackFocus();
    }
    fallbackFocus() {
      const target =
        this.querySelector("[data-saved-browse-hero]") ||
        this.stateNode?.querySelector("[data-saved-browse]");
      if (target) return target;
      // Last resort: the grid takes focus only until it loses it.
      this.grid.setAttribute("tabindex", "-1");
      this.grid.addEventListener(
        "blur",
        () => this.grid.removeAttribute("tabindex"),
        { once: true },
      );
      return this.grid;
    }
    enqueue(handle) {
      if (!this.queue.includes(handle) && !this.active.has(handle))
        this.queue.push(handle);
      this.pump();
      this.renderState();
    }
    pump() {
      while (
        this.listeners &&
        this.active.size < CONCURRENCY &&
        this.queue.length
      ) {
        const handle = this.queue.shift();
        const controller = new window.AbortController();
        this.active.set(handle, controller);
        const generation = this.generation;
        this.load(handle, controller.signal, generation).finally(() => {
          if (this.active.get(handle) === controller)
            this.active.delete(handle);
          if (generation !== this.generation) return;
          this.pump();
          this.renderState();
        });
      }
    }
    cancel(handle) {
      this.queue = this.queue.filter((item) => item !== handle);
      this.active.get(handle)?.abort();
      this.active.delete(handle);
    }
    cancelAll() {
      this.generation = (this.generation || 0) + 1;
      for (const controller of this.active?.values() || []) controller.abort();
      this.active?.clear();
      this.queue = [];
      this.querySelectorAll("[data-saved-retry]").forEach((button) =>
        button.removeAttribute("aria-disabled"),
      );
    }
    async request(url, accept, signal) {
      if (signal.aborted)
        throw new window.DOMException("Aborted", "AbortError");
      const timeout = new window.AbortController();
      const abort = () => timeout.abort();
      signal.addEventListener("abort", abort, { once: true });
      const timer = window.setTimeout(abort, TIMEOUT);
      try {
        const response = await window.fetch(url, {
          headers: { Accept: accept },
          mode: "same-origin",
          credentials: "same-origin",
          signal: timeout.signal,
        });
        return { status: response.status, body: await response.text() };
      } finally {
        window.clearTimeout(timer);
        signal.removeEventListener("abort", abort);
      }
    }
    async load(handle, signal, generation) {
      const path = `${this.root}products/${encodeURIComponent(handle)}`;
      let product = null;
      try {
        const result = await this.request(
          `${path}.js`,
          "application/json",
          signal,
        );
        if (isAbort(signal, generation, this)) return;
        if (result.status === 404) {
          window.StorefrontSaved?.remove(handle);
          return;
        }
        if (result.status !== 200) throw new Error("Product request failed");
        product = JSON.parse(result.body);
        if (
          !product ||
          typeof product !== "object" ||
          !Number.isInteger(product.id)
        )
          throw new Error("Invalid product");
        const section = await this.request(
          `${path}?section_id=sf-saved-card`,
          "text/html",
          signal,
        );
        if (isAbort(signal, generation, this)) return;
        if (section.status !== 200) throw new Error("Card request failed");
        const card = this.parseCard(section.body, product.id);
        card.dataset.savedHandle = handle;
        // A renamed product redirects; unsaving must remove the stored handle.
        card
          .querySelectorAll("[data-saved-toggle][data-product-handle]")
          .forEach((toggle) => (toggle.dataset.productHandle = handle));
        this.cache.set(handle, card);
        this.place(handle, card);
      } catch {
        if (isAbort(signal, generation, this)) return;
        this.place(handle, this.errorItem(handle, product, path));
      }
    }
    parseCard(html, id) {
      const parsed = new window.DOMParser().parseFromString(html, "text/html");
      const cards = parsed.querySelectorAll(CARD);
      const card = cards[0];
      if (
        cards.length !== 1 ||
        card.tagName !== "LI" ||
        card.dataset.productId !== String(id)
      )
        throw new Error("Invalid saved card");
      card.querySelectorAll(UNSAFE).forEach((node) => node.remove());
      for (const node of [card, ...card.querySelectorAll("*")])
        this.sanitizeAttributes(node);
      return window.document.adoptNode(card);
    }
    sanitizeAttributes(node) {
      for (const attribute of [...node.attributes]) {
        const name = attribute.name.toLowerCase();
        const unsafe =
          name.startsWith("on") ||
          name === "srcdoc" ||
          (URL_ATTRIBUTES.includes(name) && !safeURL(attribute.value)) ||
          (name === "srcset" &&
            !attribute.value
              .split(",")
              .every((candidate) => safeURL(candidate.trim().split(/\s+/)[0])));
        if (unsafe) node.removeAttribute(attribute.name);
      }
    }
    productURL(product, fallback) {
      if (typeof product?.url !== "string") return fallback;
      try {
        const url = new window.URL(product.url, window.location.href);
        return url.origin === window.location.origin ? url.href : fallback;
      } catch {
        return fallback;
      }
    }
    errorItem(handle, product, path) {
      const title =
        typeof product?.title === "string" && product.title.trim()
          ? product.title.trim()
          : handle;
      const item =
        this.querySelector("template[data-saved-error-template]")
          ?.content.cloneNode(true)
          .querySelector("[data-saved-error]") || this.fallbackErrorItem();
      item.dataset.savedHandle = handle;
      item.dataset.savedTitle = title;
      const message = item.querySelector("[data-saved-error-message]");
      if (message)
        message.textContent = fill(
          this.dataset.errorTemplate ||
            "We couldn't load {{ title }} right now.",
          "title",
          title,
        );
      const link = item.querySelector("[data-saved-error-link]");
      if (link) {
        link.href = this.productURL(product, path);
        link.textContent = title;
      }
      const retry = item.querySelector("[data-saved-retry]");
      if (retry && this.dataset.retryLabel)
        retry.textContent = this.dataset.retryLabel;
      const remove = item.querySelector("[data-saved-toggle]");
      if (remove) {
        remove.dataset.productHandle = handle;
        if (this.dataset.removeLabelTemplate)
          remove.setAttribute(
            "aria-label",
            fill(this.dataset.removeLabelTemplate, "title", title),
          );
      }
      return item;
    }
    fallbackErrorItem() {
      const create = (tag, attribute) => {
        const node = window.document.createElement(tag);
        node.setAttribute(attribute, "");
        return node;
      };
      const item = create("li", "data-saved-error");
      item.className = "sf-saved__item sf-saved__item--error";
      const retry = create("button", "data-saved-retry");
      retry.type = "button";
      retry.textContent = "Retry";
      item.append(
        create("p", "data-saved-error-message"),
        create("a", "data-saved-error-link"),
        retry,
      );
      return item;
    }
    place(handle, node) {
      const previous = this.items.get(handle);
      const active = window.document.activeElement;
      const hadFocus = previous?.contains(active);
      if (previous === this.pickedItem) this.clearPick();
      if (previous && previous !== node) previous.replaceWith(node);
      else if (!previous) this.grid.append(node);
      this.items.set(handle, node);
      this.apply();
      window.StorefrontSaved?.refresh();
      this.renderState();
      if (hadFocus)
        node
          .querySelector("[data-saved-retry], a[href]")
          ?.focus({ preventScroll: true });
    }
    filter() {
      this.clearPick();
      this.apply();
    }
    resetFilters() {
      if (this.query) this.query.value = "";
      if (this.collection) this.collection.value = "all";
      if (this.sort) this.sort.value = "featured";
      this.filter();
    }
    ordered() {
      const order = new Map(
        this.handles.map((handle, index) => [handle, index]),
      );
      const nodes = this.handles
        .map((handle) => this.items.get(handle))
        .filter(Boolean);
      const sort = this.sort?.value || "featured";
      const locale = window.document.documentElement.lang || undefined;
      const title = (node) =>
        node.dataset.title || node.dataset.savedTitle || "";
      const byTitle = (left, right) =>
        title(left).localeCompare(title(right), locale);
      const price = (node) => Number(node.dataset.price) || 0;
      return nodes.sort((left, right) => {
        const leftError = left.matches("[data-saved-error]");
        const rightError = right.matches("[data-saved-error]");
        if (leftError !== rightError) return leftError ? 1 : -1;
        let result = 0;
        if (leftError)
          return (
            order.get(left.dataset.savedHandle) -
            order.get(right.dataset.savedHandle)
          );
        if (sort === "price-ascending")
          result = price(left) - price(right) || byTitle(left, right);
        else if (sort === "price-descending")
          result = price(right) - price(left) || byTitle(left, right);
        else if (sort === "title-ascending") result = byTitle(left, right);
        return (
          result ||
          order.get(left.dataset.savedHandle) -
            order.get(right.dataset.savedHandle)
        );
      });
    }
    matches(node, terms, collection) {
      if (node.matches("[data-saved-error]")) {
        const text = normalize(node.dataset.savedTitle || "");
        return (
          collection === "all" && terms.every((term) => text.includes(term))
        );
      }
      const text = normalize(node.dataset.search || node.dataset.title || "");
      const collections = (node.dataset.collections || "").split(/\s+/);
      return (
        (collection === "all" || collections.includes(collection)) &&
        terms.every((term) => text.includes(term))
      );
    }
    apply() {
      if (!this.handles) return;
      const terms = normalize(this.query?.value || "")
        .split(/\s+/)
        .filter(Boolean);
      const collection = this.collection?.value || "all";
      const nodes = this.ordered();
      for (const node of nodes)
        node.hidden = !this.matches(node, terms, collection);
      this.visible = nodes.filter((node) => !node.hidden);
      this.arrange(nodes);
      this.renderCollectionLinks();
      this.renderState();
    }
    arrange(nodes) {
      // Move only out-of-place nodes so a focused card keeps focus.
      const active = window.document.activeElement;
      let reference = this.grid.firstElementChild;
      for (const node of nodes) {
        if (node === reference) reference = reference.nextElementSibling;
        else this.grid.insertBefore(node, reference);
      }
      if (this.stateNode) this.grid.append(this.stateNode);
      if (
        active &&
        active !== window.document.activeElement &&
        this.contains(active)
      )
        active.focus({ preventScroll: true });
    }
    renderState() {
      if (!this.grid) return;
      const loading = !this.ready || this.queue.length + this.active.size > 0;
      const empty = this.ready && this.handles.length === 0;
      // Theme Editor keeps the toolbar so merchants can select its blocks.
      const editing = Boolean(window.Shopify?.designMode);
      if (this.toolbar)
        this.toolbar.hidden = !this.ready || (empty && !editing);
      if (loading) this.grid.setAttribute("aria-busy", "true");
      else this.grid.removeAttribute("aria-busy");
      if (this.status)
        this.status.textContent =
          loading && !empty ? this.dataset.loading || "" : "";
      const visible = this.visible || [];
      const cards = visible.filter(
        (node) => !node.matches("[data-saved-error]"),
      );
      if (this.pick) this.pick.hidden = cards.length === 0;
      if (this.count)
        this.count.textContent =
          this.ready && !empty && (visible.length || !loading)
            ? fill(
                (visible.length === 1
                  ? this.dataset.countOneTemplate
                  : this.dataset.countTemplate) || "{{ count }} canvases",
                "count",
                visible.length,
              )
            : "";
      let state = null;
      if (empty) state = "empty";
      else if (this.ready && !visible.length && (this.items.size || !loading))
        state = "no-results";
      this.renderStateNode(state);
    }
    renderStateNode(state) {
      if (this.stateNode?.dataset.savedState === state) return;
      this.stateNode?.remove();
      this.stateNode = null;
      if (!state) return;
      const selector =
        state === "empty"
          ? "template[data-saved-empty-template]"
          : "template[data-saved-no-results-template]";
      const template = this.querySelector(selector);
      if (!template) return;
      const item = window.document.createElement("li");
      item.className = "sf-saved__state";
      item.dataset.savedState = state;
      item.style.gridColumn = "1 / -1";
      item.append(template.content.cloneNode(true));
      this.stateNode = item;
      this.grid.append(item);
    }
    clearPick() {
      this.pickedItem?.classList.remove("sf-saved__item--picked");
      this.pickedItem = null;
      if (this.picked) this.picked.textContent = "";
    }
    pickOne() {
      const cards = (this.visible || []).filter(
        (node) => !node.matches("[data-saved-error]") && node.isConnected,
      );
      if (!cards.length) return;
      this.clearPick();
      const card = cards[Math.floor(Math.random() * cards.length)];
      this.pickedItem = card;
      card.classList.add("sf-saved__item--picked");
      const link = card.querySelector("a[href]");
      link?.focus({ preventScroll: true });
      card.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "instant"
          : "smooth",
        block: "center",
      });
      const title = card.dataset.title || link?.textContent.trim() || "";
      if (this.picked)
        this.picked.textContent = fill(
          this.dataset.pickedTemplate || "Picked {{ title }}",
          "title",
          title,
        );
    }
  }
  if (!window.customElements.get("sf-saved-page"))
    window.customElements.define("sf-saved-page", StorefrontSavedPage);
})();
